/* ============================================================
 * Collection Bundle 2026 — "Build Your Bundle" (ENG-7632)
 *
 * Custom element <bundle-builder-2026> parameterized via `data-*`
 * (travel/sampler mode, minimum checkout qty, tiers, max). Does NOT
 * touch the legacy `collection-bundle.js`.
 *
 * CART CONTRACT: the bundle is kept in REAL-TIME SYNC with the live cart.
 * Each selection immediately POSTs the variant to /cart/add.js and each
 * de-selection POSTs quantity:0 to /cart/change.js, so the bottom bar and the
 * cart always show the same items. Does NOT apply a discount code or line item
 * properties — the discount is applied by a Shopify Automatic Discount that
 * matches the variants in the cart. The CTA just redirects to /checkout (the
 * items are already in the cart).
 *
 * The visible cart is the Rebuy Smart Cart flyout (the theme's <cart-drawer>
 * is disabled: settings.cart_drawer_enabled = false). Rebuy does NOT intercept
 * native fetch('/cart/*.js'), so after every mutation we re-sync Rebuy's
 * internal cart (Cart.fetchShopifyCart → SmartCart.setCart). Changes made
 * inside the flyout (remove / +/-) fire `rebuy:smartcart.*` DOM events, which
 * we listen for to rebuild the selection from /cart.js — bidirectional sync.
 *
 * SUPPRESSED AUTO-OPEN: Rebuy's "open cart after adding" behavior would pop the
 * flyout open on every bundle add/remove, which is jarring on a page whose whole
 * job is building a selection inline. So we suppress the flyout's `show` only
 * when it was triggered by a builder mutation (a short time window around each
 * add/remove). Opening the flyout by any other means — the header cart icon —
 * still works. This suppression lives inside the custom element, which only ever
 * mounts on the two `collection-bundle-2026` templates (build-sampler-kit-2026 /
 * build-travel-bundle-2026), so it never affects the cart anywhere else.
 *
 * The in-memory `selected` is reconciled FROM the cart on load and on bfcache
 * restore, so the bar reflects whatever the cart already holds.
 * ============================================================ */

class BundleBuilder2026 extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;

    this.mode = this.dataset.bundleMode || 'travel';
    this.checkoutMinQty = parseInt(this.dataset.checkoutMinQty, 10) || 1;

    try {
      this.tiers = JSON.parse(this.dataset.tiers || '[]');
    } catch (e) {
      this.tiers = [];
    }
    this.tiers.sort((a, b) => a.qty - b.qty);

    // ENG-8122 — the control ladder stays in `data-tiers` (the section blocks, which `max_qty`
    // is derived from); the treatment arms arrive pre-validated in `data-nw-tiers`, resolved by
    // snippets/tier-discount-pct.liquid. Absent attribute = experiment off = control only.
    this.controlTiers = this.tiers;
    try {
      this.armTiers = JSON.parse(this.dataset.nwTiers || '{}');
      // Inside the guard on purpose: a payload that parses but is not an object of arrays would
      // throw here, and `_init` is already true — the element would stay mounted but dead (no
      // click listeners, no cart sync, inert CTA). Falling back to control is the safe read.
      Object.keys(this.armTiers).forEach((k) => this.armTiers[k].sort((a, b) => a.qty - b.qty));
    } catch (e) {
      this.armTiers = {};
    }
    this.applyCohortTiers();

    // `max_qty` is deliberately NOT recalculated from the cohort ladder: it stays derived from
    // the blocks. Every arm carries the same quantities by construction (the section emits them
    // by iterating the same blocks), so the number of slots does not depend on the cohort.
    this.maxQty =
      parseInt(this.dataset.maxQty, 10) ||
      (this.controlTiers.length
        ? this.controlTiers[this.controlTiers.length - 1].qty
        : 4);

    // Sampler: flat kit price in cents (injected by the section).
    this.kitPrice = this.dataset.kitPrice ? Number(this.dataset.kitPrice) : null;

    // State: array of items in selection order (mirrors the live cart).
    this.selected = [];

    this.root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    // Guards a builder-initiated cart mutation so the `rebuy:*` events it
    // triggers don't reconcile against a not-yet-settled cart.
    this._mutating = 0;

    // Epoch (ms) until which a `rebuy:smartcart.show` is treated as auto-open
    // triggered by a builder mutation, and closed. 0 = don't suppress.
    this._suppressOpenUntil = 0;

    this.bar = this.querySelector('#bybBar');
    this.thumbsEl = this.querySelector('#bybThumbs');
    this.msgEl = this.querySelector('#bybMsg');
    this.originalEl = this.querySelector('#bybOriginal');
    this.finalEl = this.querySelector('#bybFinal');
    this.cta = this.querySelector('#bybCta');
    this.pills = Array.from(this.querySelectorAll('#bybTiers .byb-pill'));

    this.bindEvents();
    // The server already knew the cohort on a repeat pageview, so applyCohortTiers() above may
    // have swapped the ladder before anything was painted. Write it into the pills, which are
    // server-rendered Liquid and therefore still show control.
    this.renderTierPills();
    this.syncUI();

    // Seed the selection from whatever the cart already holds (e.g. a returning
    // customer, or an abandoned-then-resumed session).
    this.reconcileFromCart();
  }

  bindEvents() {
    this.querySelectorAll('.byb-card').forEach((card) => {
      const addBtn = card.querySelector('.byb-card-add');
      if (!addBtn || addBtn.disabled) return;

      addBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggle(card);
      });

      const img = card.querySelector('.byb-card-img');
      if (img) {
        img.addEventListener('click', () => this.toggle(card));
      }
    });

    if (this.cta) {
      this.cta.addEventListener('click', () => this.handleCheckout());
    }

    // When the customer clicks the CTA we lock it into a `loading` state
    // (pointer-events: none + disabled) and redirect to /checkout. If they
    // then navigate back, the browser may restore this page from the bfcache
    // with that frozen DOM/JS state — leaving the CTA stuck in `loading` so
    // it silently ignores clicks even after selecting more items. Reset the
    // checkout state and re-sync from the cart on a bfcache restore.
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) {
        this.resetCheckoutState();
        this.reconcileFromCart();
      }
    });

    // Bidirectional sync: changes made inside the Rebuy flyout (remove a line,
    // +/- quantity) or any external add fire these DOM events. Rebuy re-renders
    // asynchronously (~500ms), so wait before re-reading /cart.js. Skip while a
    // builder-initiated mutation is in flight — our optimistic state is already
    // correct and the cart may not have settled yet.
    ['rebuy:smartcart.line-item-removed',
     'rebuy:smartcart.line-item-decrease',
     'rebuy:smartcart.line-item-increase',
     'rebuy:cart.add'].forEach((evt) => {
      document.addEventListener(evt, () => {
        if (this._mutating) return;
        setTimeout(() => this.reconcileFromCart(), 500);
      });
    });

    // ENG-8122 — ENG-8074's signal: assets/pricing-flash.js fires this once GrowthBook has
    // resolved, which is the same decision that drives the cart write. applyCohortTiers() is
    // also called on connect, so both load orders are covered and there is no dependency on
    // which deferred script ran first.
    document.addEventListener('nw:pricing-cohort', () => {
      this.applyCohortTiers();
      this.refreshSelectedPrices();
      this.renderTierPills();
      this.syncUI();
    });

    // Suppress Rebuy's auto-open: when a builder add/remove causes the flyout to
    // show, close it. A manual open (header cart icon) fires the same event but
    // outside the suppression window, so it stays open.
    document.addEventListener('rebuy:smartcart.show', () => {
      if (Date.now() <= this._suppressOpenUntil) this.closeSmartCart();
    });
  }

  // Close the Rebuy Smart Cart flyout if it exposes a hide()/close() API.
  // Feature-detected and best-effort — silently no-ops if Rebuy is absent.
  closeSmartCart() {
    const sc = window.Rebuy && window.Rebuy.SmartCart;
    if (!sc) return;
    try {
      if (typeof sc.hide === 'function') sc.hide();
      else if (typeof sc.close === 'function') sc.close();
    } catch (e) {}
  }

  resetCheckoutState() {
    if (this.cta) {
      this.cta.classList.remove('loading');
      this.cta.setAttribute('aria-busy', 'false');
    }
    this.syncUI();
  }

  cardData(card) {
    const btn = card.querySelector('.byb-card-add');
    return {
      product_id: Number(btn.dataset.productId),
      product_title: btn.dataset.productTitle,
      variant_id: Number(btn.dataset.variantId),
      variant_img: btn.dataset.variantImg,
      variant_price: this.unitPrice(btn),
    };
  }

  // ENG-8122 — the price this visitor is charged for one unit, in cents.
  //
  // `data-variant-price` is the SERVER render, which is control on the first pageview of a new
  // visitor (the cohort attribute does not exist yet). The cards themselves converge via the
  // anti-flash hooks, but this attribute has none — so before this, the bottom bar summed
  // control cents forever while the cards showed cohort prices.
  //
  // nwPricingLookup is assets/pricing-flash.js's own lookup, rails included. Calling it rather
  // than re-reading window.nwPricing keeps one parser of that artefact, the same reason
  // assets/search-2026.js uses it.
  unitPrice(btn) {
    const server = Number(btn.dataset.variantPrice);
    const catalog = Number(btn.dataset.variantCatalogPrice);
    if (typeof window.nwPricingLookup !== 'function' || !catalog) return server;
    const cohort =
      typeof window.nwPricingCohort === 'function' ? window.nwPricingCohort() : '';
    const hit = window.nwPricingLookup(cohort, btn.dataset.variantId, catalog);
    return hit === null ? catalog : hit;
  }

  // The ladder for the arm the page is converging on. Falls back to control for an arm with no
  // ladder — which is the state the site is in before the discount cutover, and the one the
  // generated artefact is asserted non-empty to prevent after it.
  applyCohortTiers() {
    const cohort =
      typeof window.nwPricingCohort === 'function' ? window.nwPricingCohort() : '';
    const arm = cohort ? this.armTiers[cohort] : null;
    // `[]` is truthy: an arm emitted empty has to fall back too, otherwise tierForCount()
    // returns null and the bar shows 0% with an empty message.
    const next = arm && arm.length ? arm : this.controlTiers;
    if (next === this.tiers) return false;
    this.tiers = next;
    return true;
  }

  // Re-read each selected item's price from its card. The entries cache the price at toggle
  // time, and `selected` only ever holds variants that are on this page (reconcileFromCart
  // limits it to variantCardMap()), so the card is always there to read.
  refreshSelectedPrices() {
    this.selected.forEach((entry) => {
      const btn = this.querySelector(
        `.byb-card-add[data-variant-id="${entry.variant_id}"]`
      );
      if (btn) entry.variant_price = this.unitPrice(btn);
    });
  }

  // The pills are server-rendered Liquid, so the cohort ladder has to be written into them.
  // Only the digits are replaced, never the whole string — same rule as applySaveNode() in
  // assets/pricing-flash.js, so any surrounding wording survives.
  renderTierPills() {
    this.pills.forEach((pill) => {
      const qty = parseInt(pill.dataset.tier, 10);
      const tier = this.tiers.find((t) => t.qty === qty);
      const node = pill.querySelector('.byb-pill-pct');
      if (!tier || !node) return;
      const text = node.textContent;
      if (/\d/.test(text)) node.textContent = text.replace(/\d+/, String(tier.discount));
    });
  }

  variantIdOf(card) {
    const btn = card.querySelector('.byb-card-add');
    return btn ? Number(btn.dataset.variantId) : null;
  }

  indexOfVariant(variantId) {
    return this.selected.findIndex((e) => e.variant_id === variantId);
  }

  toggle(card) {
    const addBtn = card.querySelector('.byb-card-add');
    if (!addBtn || addBtn.disabled) return;

    const variantId = this.variantIdOf(card);
    const idx = this.indexOfVariant(variantId);

    // Optimistically update the bar for instant feedback, then mirror the
    // change to the live cart. If the cart write fails, revert.
    if (idx > -1) {
      const removed = this.selected[idx];
      this.selected.splice(idx, 1);
      this.syncUI();
      this.mutateCart('remove', removed.variant_id, () => {
        this.selected.splice(idx, 0, removed);
        this.syncUI();
      });
    } else {
      if (this.selected.length >= this.maxQty) return;
      const data = this.cardData(card);
      this.selected.push(data);
      this.syncUI();
      this.mutateCart('add', data.variant_id, () => {
        const i = this.indexOfVariant(data.variant_id);
        if (i > -1) this.selected.splice(i, 1);
        this.syncUI();
      });
    }
  }

  // Map of every purchasable variant on the page → its card data, so we can
  // rebuild the selection from a /cart.js payload.
  variantCardMap() {
    const map = new Map();
    this.querySelectorAll('.byb-card').forEach((card) => {
      const btn = card.querySelector('.byb-card-add');
      if (!btn || btn.disabled) return;
      map.set(Number(btn.dataset.variantId), this.cardData(card));
    });
    return map;
  }

  // Add or remove a single unit of `variantId` in the live cart, then re-sync
  // the Rebuy flyout. `onError` reverts the optimistic bar update.
  mutateCart(action, variantId, onError) {
    this._mutating += 1;

    // Open the suppression window: the auto-open fires asynchronously after the
    // cart write + Rebuy re-sync, so keep it wide enough to catch that late show.
    this._suppressOpenUntil = Date.now() + 2500;
    const done = () => {
      this._mutating = Math.max(0, this._mutating - 1);
    };

    let request;
    if (action === 'add') {
      request = fetch(this.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });
    } else {
      // change.js keyed by variant id removes the whole line (there is only ever
      // one line per bundle variant, quantity 1).
      request = fetch(this.root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: String(variantId), quantity: 0 }),
      });
    }

    return request
      .then((res) => {
        if (!res.ok) throw new Error('cart ' + action + ' responded ' + res.status);
        return res.json();
      })
      .then(() => {
        this.syncRebuyCart();
      })
      .catch(() => {
        if (typeof onError === 'function') onError();
        if (this.msgEl) {
          this.msgEl.innerHTML =
            this.dataset.checkoutError ||
            'Something went wrong updating your cart. Please try again.';
        }
      })
      .then(done, done);
  }

  // Rebuy does not observe native /cart/*.js fetches, so after a headless
  // mutation we re-sync its internal cart from Shopify. Best-effort and
  // feature-detected — it silently no-ops if Rebuy is absent or mid-init.
  syncRebuyCart() {
    const rb = window.Rebuy;
    if (!rb || !rb.Cart || !rb.SmartCart) return;
    if (typeof rb.Cart.fetchShopifyCart !== 'function' || typeof rb.SmartCart.setCart !== 'function') {
      return;
    }
    try {
      const maybe = rb.Cart.fetchShopifyCart((cart) => {
        try { rb.SmartCart.setCart(cart); } catch (e) {}
      });
      if (maybe && typeof maybe.then === 'function') {
        maybe.then((cart) => { try { rb.SmartCart.setCart(cart); } catch (e) {} });
      }
    } catch (e) {}
  }

  // Rebuild the in-memory selection from the live cart (source of truth),
  // limited to variants present on this page and capped at maxQty. Keeps the
  // richer card data (image / title / price) rather than the leaner cart line.
  //
  // ORDER-STABLE: the bottom-bar order is the selection order, NOT Shopify's
  // cart.items order (which can shuffle after change.js). So we keep the current
  // selection order for items still in the cart, then append any items added
  // elsewhere (Rebuy flyout, another tab) in cart order. Without this, every
  // reconcile after an add would reshuffle the existing thumbs to match the
  // cart's order instead of leaving them in place.
  reconcileFromCart() {
    const pageVariants = this.variantCardMap();
    if (!pageVariants.size) return;

    fetch(this.root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then((res) => (res.ok ? res.json() : null))
      .then((cart) => {
        if (!cart) return;

        // Variants on this page that the cart currently holds.
        const inCart = new Set();
        (cart.items || []).forEach((item) => {
          if (pageVariants.has(item.variant_id)) inCart.add(item.variant_id);
        });

        const next = [];
        const seen = new Set();

        // 1. Preserve the existing selection order for items still in the cart.
        this.selected.forEach((entry) => {
          if (inCart.has(entry.variant_id) && !seen.has(entry.variant_id)) {
            seen.add(entry.variant_id);
            next.push(entry);
          }
        });

        // 2. Append items added outside the builder, in cart order.
        (cart.items || []).forEach((item) => {
          if (inCart.has(item.variant_id) && !seen.has(item.variant_id)) {
            seen.add(item.variant_id);
            next.push(pageVariants.get(item.variant_id));
          }
        });

        this.selected = next.slice(0, this.maxQty);
        // variantCardMap() above froze each price BEFORE this fetch. If nw:pricing-cohort landed
        // in between, its handler ran refreshSelectedPrices() against a still-empty `selected`,
        // and the entries just assigned carry the pre-cohort snapshot. Re-resolve from the cards,
        // which the handler has already updated.
        this.refreshSelectedPrices();
        this.syncUI();
      })
      .catch(() => {});
  }

  toggleByVariant(variantId) {
    const btn = this.querySelector(`.byb-card-add[data-variant-id="${variantId}"]`);
    const card = btn ? btn.closest('.byb-card') : null;
    if (card) this.toggle(card);
  }

  tierForCount(n) {
    let active = null;
    this.tiers.forEach((t) => {
      if (n >= t.qty) active = t;
    });
    return active;
  }

  nextTier(n) {
    return this.tiers.find((t) => t.qty > n) || null;
  }

  formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, this.dataset.moneyFormat);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  syncUI() {
    const count = this.selected.length;
    const selectedIds = this.selected.map((e) => e.variant_id);

    // ── Cards: selected / position / disabled / button label
    this.querySelectorAll('.byb-card').forEach((card) => {
      const addBtn = card.querySelector('.byb-card-add');
      if (!addBtn) return;
      const vid = Number(addBtn.dataset.variantId);
      const i = selectedIds.indexOf(vid);
      const sel = i > -1;

      card.classList.toggle('selected', sel);
      card.classList.toggle('disabled', !sel && count >= this.maxQty && !addBtn.disabled);

      const pos = card.querySelector('[data-pos]');
      if (pos) pos.textContent = sel ? String(i + 1) : '';

      if (!addBtn.disabled) {
        addBtn.setAttribute('aria-pressed', sel ? 'true' : 'false');
        const label = addBtn.querySelector('.byb-card-add-label');
        if (label && addBtn.dataset.textAdd) {
          label.textContent = sel ? addBtn.dataset.textAdded : addBtn.dataset.textAdd;
        }
      }
    });

    // ── Tier pills (travel)
    const activeTier = this.tierForCount(count);
    this.pills.forEach((pill) => {
      const t = parseInt(pill.dataset.tier, 10);
      const unlocked = count >= t;
      const isActive = activeTier && activeTier.qty === t;
      pill.classList.toggle('unlocked', unlocked && !isActive);
      pill.classList.toggle('active', !!isActive);
    });

    // ── Bar visibility + thumbs
    if (this.bar) this.bar.classList.toggle('visible', count > 0);
    this.renderThumbs(count);

    // ── Price + message + CTA (per mode)
    if (this.mode === 'sampler') {
      this.syncSampler(count);
    } else {
      this.syncTravel(count);
    }
  }

  renderThumbs(count) {
    if (!this.thumbsEl) return;
    this.thumbsEl.innerHTML = '';

    for (let i = 0; i < this.maxQty; i++) {
      if (i < count) {
        const item = this.selected[i];
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'byb-thumb';
        btn.setAttribute('aria-label', `Remove ${item.product_title} from bundle`);

        const img = document.createElement('img');
        img.className = 'byb-thumb-img';
        img.loading = 'lazy';
        img.src = item.variant_img;
        img.alt = item.product_title || '';
        btn.appendChild(img);

        const remove = document.createElement('span');
        remove.className = 'byb-thumb-remove';
        remove.setAttribute('aria-hidden', 'true');
        remove.innerHTML =
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>';
        btn.appendChild(remove);

        btn.addEventListener('click', () => this.toggleByVariant(item.variant_id));
        this.thumbsEl.appendChild(btn);
      } else {
        const empty = document.createElement('div');
        empty.className = 'byb-thumb-empty';
        this.thumbsEl.appendChild(empty);
      }
    }
  }

  syncTravel(count) {
    const subtotal = this.selected.reduce((acc, e) => acc + e.variant_price, 0);
    const tier = this.tierForCount(count);
    const discount = tier ? tier.discount : 0;
    const final = discount > 0 ? subtotal * (1 - discount / 100) : subtotal;

    // Price block
    if (this.originalEl && this.finalEl) {
      if (count === 0) {
        this.originalEl.textContent = '';
        this.finalEl.innerHTML = this.formatMoney(0);
      } else if (discount > 0) {
        this.originalEl.textContent = this.formatMoney(subtotal);
        this.finalEl.innerHTML =
          this.formatMoney(final) + ' <small>' + discount + '% off</small>';
      } else {
        this.originalEl.textContent = '';
        this.finalEl.innerHTML = this.formatMoney(final);
      }
    }

    // Message
    if (this.msgEl) {
      if (count === 0) {
        this.msgEl.innerHTML = this.tiers
          .map((t) => '<strong>' + t.discount + '%</strong> off at ' + t.qty)
          .join(' · ');
      } else if (count >= this.maxQty) {
        this.msgEl.innerHTML = 'Bundle full.';
      } else {
        const next = this.nextTier(count);
        if (next) {
          const remaining = next.qty - count;
          this.msgEl.innerHTML =
            'Add <strong>' +
            remaining +
            ' more</strong> for <strong>' +
            next.discount +
            '% off</strong>.';
        } else {
          this.msgEl.innerHTML = 'Bundle full.';
        }
      }
    }

    // CTA
    this.updateCta(count >= this.checkoutMinQty);
  }

  syncSampler(count) {
    const complete = count >= this.maxQty;
    const price = this.kitPrice != null ? this.kitPrice : 0;

    // Flat kit price — muted until the kit is complete.
    if (this.originalEl) this.originalEl.textContent = '';
    if (this.finalEl) {
      const money = this.formatMoney(price);
      this.finalEl.innerHTML = complete
        ? money
        : '<span class="byb-price-pending">' + money + '</span>';
    }

    // Message
    if (this.msgEl) {
      const remaining = this.maxQty - count;
      if (remaining === this.maxQty) {
        this.msgEl.innerHTML = 'Add <strong>' + this.maxQty + ' samples</strong> to build your kit.';
      } else if (remaining > 0) {
        this.msgEl.innerHTML = 'Add <strong>' + remaining + ' more</strong> to complete your kit.';
      } else {
        this.msgEl.innerHTML =
          this.dataset.completeMessage ||
          'Kit complete — your credit applies to your next purchase.';
      }
    }

    // CTA — only when exactly complete
    this.updateCta(complete);
  }

  updateCta(enabled) {
    if (!this.cta) return;
    this.cta.disabled = !enabled;
    const svg = this.cta.querySelector('svg');
    if (svg) svg.style.display = enabled ? 'inline' : 'none';
  }

  handleCheckout() {
    if (this.selected.length < this.checkoutMinQty) return;

    this.cta.classList.add('loading');
    this.cta.disabled = true;
    this.cta.setAttribute('aria-busy', 'true');

    // The selected variants are already in the cart (added on each toggle via
    // real-time sync), so we don't re-POST them — that would duplicate lines.
    // The Automatic Discount matches the variants already present. Wait for any
    // in-flight toggle to settle first, so a fast checkout click can't leave the
    // most recent item out of the cart. If the customer navigates back from
    // checkout, the `pageshow` handler clears this loading state and re-syncs.
    this.whenCartSettled().then(() => {
      window.location.href = this.root + 'checkout';
    });
  }

  // Resolves once no builder-initiated cart mutation is in flight (or after a
  // safety timeout so the CTA can never hang).
  whenCartSettled(timeout = 3000) {
    return new Promise((resolve) => {
      const start = Date.now();
      const tick = () => {
        if (this._mutating === 0 || Date.now() - start > timeout) {
          resolve();
        } else {
          setTimeout(tick, 60);
        }
      };
      tick();
    });
  }
}

if (!customElements.get('bundle-builder-2026')) {
  customElements.define('bundle-builder-2026', BundleBuilder2026);
}
